/**
 * Calendar Sync API client
 * Handles Google and Apple calendar integrations
 */

import { request } from "./client";

export interface CalendarIntegration {
  id: string;
  provider: "google" | "apple";
  status: "connected" | "disconnected" | "error";
  last_synced_at: string | null;
  settings: Record<string, any>;
  created_at: string;
  calendar_id?: string;
}

export interface GoogleCalendarInfo {
  id: string;
  summary: string;
  primary: boolean;
}

export interface CalendarSourceInfo {
  provider: string;
  calendar_id?: string;
  calendar_name?: string;
}

export interface BandsintownLinkInfo {
  matched: boolean;
  url?: string;
  event_id?: string;
  confidence: number;
  reasons: string[];
}

export interface UnifiedEvent {
  id: string;
  starts_at: string;
  ends_at?: string;
  timezone?: string;
  title?: string;
  location?: string;
  calendar_provider: string;
  calendar_source: CalendarSourceInfo;
  bandsintown: BandsintownLinkInfo;
}

export interface GoogleOAuthStartResponse {
  auth_url: string;
}

export interface CalendarSyncResult {
  success: boolean;
  provider: string;
  events_synced: number;
  last_synced_at: string;
}

/**
 * Get all calendar integrations for current user
 */
export async function getCalendarIntegrations(artistId?: string): Promise<CalendarIntegration[]> {
  return request<CalendarIntegration[]>("/calendar/integrations", { headers: artistId ? { "X-Artist-Id": artistId } : {} });
}

/**
 * Start Google OAuth flow
 * Returns the authorization URL to redirect user to
 */
export async function startGoogleOAuth(): Promise<GoogleOAuthStartResponse> {
  return request<GoogleOAuthStartResponse>("/calendar/google/connect/start", {
    method: "POST",
  });
}

/**
 * Complete Google OAuth callback
 * Exchange authorization code for tokens
 */
export async function completeGoogleOAuth(
  code: string,
  redirectUri: string
): Promise<CalendarIntegration> {
  return request<CalendarIntegration>("/calendar/google/connect/callback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  });
}

/**
 * List available Google calendars for selection
 */
export async function listGoogleCalendars(): Promise<GoogleCalendarInfo[]> {
  return request<GoogleCalendarInfo[]>("/calendar/google/calendars");
}

/**
 * Select which Google calendar to sync
 */
export async function selectGoogleCalendar(calendarId: string): Promise<{ success: boolean }> {
  const formData = new FormData();
  formData.append("calendar_id", calendarId);

  return request<{ success: boolean }>("/calendar/google/select-calendar", {
    method: "POST",
    body: formData,
  });
}

/**
 * Connect Apple calendar via ICS URL or file upload
 */
export async function connectAppleCalendar(
  icsUrl?: string,
  icsFile?: File
): Promise<CalendarIntegration> {
  const formData = new FormData();

  if (icsUrl) {
    formData.append("ics_url", icsUrl);
  }

  if (icsFile) {
    formData.append("ics_file", icsFile);
  }

  return request<CalendarIntegration>("/calendar/apple/connect", {
    method: "POST",
    body: formData,
  });
}

/**
 * Run calendar sync for a provider
 */
export async function runCalendarSync(
  provider: "google" | "apple",
  timeMin?: string,
  timeMax?: string,
  artistId?: string
): Promise<CalendarSyncResult> {
  return request<CalendarSyncResult>("/calendar/sync/run", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(artistId ? { "X-Artist-Id": artistId } : {}) },
    body: JSON.stringify({
      provider,
      time_min: timeMin,
      time_max: timeMax,
    }),
  });
}

/**
 * Get unified calendar events with Bandsintown matching
 */
export async function getUnifiedCalendarEvents(
  startDate?: string,
  endDate?: string,
  artistId?: string
): Promise<UnifiedEvent[]> {
  const params = new URLSearchParams();

  if (startDate) {
    params.append("start_date", startDate);
  }

  if (endDate) {
    params.append("end_date", endDate);
  }

  const url = `/calendar/events${params.toString() ? `?${params.toString()}` : ""}`;
  return request<UnifiedEvent[]>(url, { headers: artistId ? { "X-Artist-Id": artistId } : {} });
}

/**
 * Disconnect a calendar integration
 */
export async function disconnectCalendarIntegration(
  integrationId: string
): Promise<{ success: boolean; message: string }> {
  return request<{ success: boolean; message: string }>(
    `/calendar/integrations/${integrationId}`,
    {
      method: "DELETE",
    }
  );
}
